import { ReactNode, SyntheticEvent } from 'react';
import { Close } from '@mui/icons-material';
import {
  CancelButton,
  CloseButton,
  DialogButtons,
  StyledDialog,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogHeader,
  SubmitButton,
  Title,
} from './styles';
import { Checkbox } from '@/components/atoms/checkbox';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';

type ModalProps = {
  modalTitle: string;
  open: boolean;
  children: ReactNode;
  className?: string;
  checkbox?: {
    name: string;
    label: string;
    onChange: (event: SyntheticEvent<Element, Event>, checked: boolean) => void;
  };
  submitButton: {
    text: string;
    loading?: boolean;
    disabled?: boolean;
    onClick?: () => void;
  };
  cancelButton?: {
    text: string;
    disabled?: boolean;
    onClick?: () => void;
  };
  onClose: () => void;
};

export const Modal = ({
  modalTitle,
  open,
  children,
  className,
  checkbox,
  submitButton,
  cancelButton,
  onClose,
}: ModalProps) => {
  return (
    <StyledDialog onClose={onClose} aria-labelledby="dialog" open={open} data-testid="dialog">
      <div className={className}>
        <StyledDialogHeader>
          <Title variant="h6">
            <SafeMarkdown text={modalTitle} />
          </Title>

          <CloseButton aria-label="close" onClick={onClose} variant="text">
            <Close />
          </CloseButton>
        </StyledDialogHeader>

        <StyledDialogContent dividers>{children}</StyledDialogContent>

        <StyledDialogActions>
          {checkbox && (
            <Checkbox name={checkbox.name} label={checkbox.label} onChange={checkbox.onChange} />
          )}

          <DialogButtons>
            {cancelButton && (
              <CancelButton
                data-testid="modal-cancel-button"
                autoFocus
                onClick={cancelButton.onClick}
                disabled={cancelButton.disabled}
                variant="text"
              >
                <SafeMarkdown text={cancelButton.text} />
              </CancelButton>
            )}

            <SubmitButton
              data-testid="modal-submit-button"
              loading={submitButton.loading}
              disabled={submitButton.disabled}
              variant="contained"
              onClick={submitButton.onClick}
            >
              <SafeMarkdown text={submitButton.text} />
            </SubmitButton>
          </DialogButtons>
        </StyledDialogActions>
      </div>
    </StyledDialog>
  );
};
